import {
  Controller as TabsController,
  ListingPageHeader,
  ModalTab,
} from 'components/Shared';
import type {
  VehicleJourneyCatalogs,
  YardFlow,
  YardFlowCompositionPayload,
  YardCatalogItem,
  YardDashboardIndicatorRole,
  YardStepType,
} from 'contracts/VehicleJourney';
import type { YardRole } from 'contracts/YardRoles';
import { useVehicleJourneyConfiguration } from 'hooks/api/useVehicleJourneyConfiguration';
import { Scaffold } from 'layouts';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  GripVertical,
  Plus,
  Search,
  Settings,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { apiErrorHandler, notify } from 'services';
import { Button } from 'styles/components';
import { validateJourneyFlow } from 'validators/VehicleJourney/CreateFlow';
import {
  emptyJourneyFlow,
  FIELD_TYPES,
  JourneyField,
  JourneyFlow,
  JourneyStep,
  INDICATOR_ROLES,
  STEP_TYPE_LABELS,
  STEP_TYPES,
  toJourneyFlow,
} from './config';
import * as S from './styles';
import { CarrierGroupsTab } from './carrierGroupsTab';
import { CustomerExceptionsTab } from './customerExceptionsTab';

const emptyCatalogs: VehicleJourneyCatalogs = {
  warehouses: [],
  orderTypes: [],
  carriers: [],
  carrierGroups: [],
  steps: [],
};

const sanitizeFlowName = (value: string) =>
  value.replace(/[^a-zA-ZÀ-ÿ0-9_ ]/g, '');

const normalizeCode = (value: string) =>
  sanitizeFlowName(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ /g, '_')
    .toUpperCase()
    .slice(0, 50);

const temporaryId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const fieldHasOptions = (field: JourneyField) =>
  field.type === 'SELECT' || field.type === 'CHECKBOX_GROUP';

const fieldPreview = (field: JourneyField): React.ReactNode => {
  if (fieldHasOptions(field)) {
    return (
      <select
        aria-label={`Pré-visualização ${field.name}`}
        className="w-full max-w-[220px] rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-500"
      >
        <option>Selecione uma opção</option>
        {field.options.map((option) => (
          <option key={option.id}>{option.label}</option>
        ))}
      </select>
    );
  }

  if (field.type === 'CHECKBOX') {
    return (
      <label className="flex items-center gap-1.5 text-xs text-slate-600">
        <input type="checkbox" className="accent-[#EE7C2C]" /> {field.name}
      </label>
    );
  }

  return (
    <input
      aria-label={`Pré-visualização ${field.name}`}
      className="max-w-[180px] rounded border border-slate-300 px-2 py-1 text-xs"
      placeholder={field.placeholder || field.name}
      type={
        field.type === 'NUMBER'
          ? 'number'
          : field.type === 'TIME'
            ? 'time'
            : 'text'
      }
    />
  );
};

const validateComposition = (flow: JourneyFlow): string | null => {
  if (!flow.steps.length) return 'Adicione ao menos uma etapa ao fluxo.';
  if (!flow.steps.some((step) => step.active))
    return 'Mantenha ao menos uma etapa ativa.';

  const firstActiveStep = flow.steps.find((step) => step.active);
  if (firstActiveStep?.interactionMode !== 'START_FINISH') {
    return 'A primeira etapa ativa deve usar o modo Iniciar/finalizar.';
  }

  for (const step of flow.steps) {
    const codes = step.fields.map((field) => field.code.trim());
    if (codes.some((code) => !code))
      return `Informe o código de todos os campos de ${step.name}.`;
    if (new Set(codes).size !== codes.length)
      return `Os códigos dos campos de ${step.name} não podem se repetir.`;

    for (const field of step.fields) {
      if (!field.name.trim())
        return `Informe o nome do campo ${field.code || 'sem código'}.`;
      if (
        fieldHasOptions(field) &&
        !field.options.some((option) => option.active && option.label.trim())
      ) {
        return `Adicione ao menos uma opção ao campo ${field.name}.`;
      }
      const optionCodes = field.options.map((option) => option.code);
      if (
        fieldHasOptions(field) &&
        (optionCodes.some((code) => !code) ||
          new Set(optionCodes).size !== optionCodes.length)
      ) {
        return `As opções do campo ${field.name} devem possuir valores diferentes.`;
      }
    }
  }

  return null;
};

const compositionPayload = (flow: JourneyFlow): YardFlowCompositionPayload => ({
  steps: flow.steps.map((step, stepIndex) => ({
    yardStepId: step.yardStepId,
    yardRoleId: step.yardRoleId,
    sequence: stepIndex + 1,
    interactionMode: step.interactionMode,
    completionTrigger: step.completionTrigger,
    hasBip: step.hasBip,
    hasCoordinates:
      step.completionTrigger === 'ENTRY_AUTHORIZED'
        ? false
        : step.hasCoordinates,
    hasReject: step.hasReject,
    required: step.required,
    active: step.completionTrigger === 'ENTRY_AUTHORIZED' ? true : step.active,
    fields: step.fields.map((field, fieldIndex) => ({
      code: field.code.trim(),
      label: field.name.trim(),
      type: field.type,
      required:
        step.completionTrigger === 'ENTRY_AUTHORIZED' ? false : field.required,
      active: field.active,
      sequence: fieldIndex + 1,
      placeholder: field.placeholder.trim() || null,
      helpText: field.helpText.trim() || null,
      settings: null,
      options: fieldHasOptions(field)
        ? field.options.map((option, optionIndex) => ({
            code: option.code,
            label: option.label.trim(),
            description: null,
            sequence: optionIndex + 1,
            active: option.active,
          }))
        : [],
    })),
  })),
});

export const VehicleJourneyPage: React.FC = () => {
  const {
    createFlow,
    createCarrierGroup,
    createCarrierGroupRule,
    createStep,
    deleteFlow,
    getCatalogs,
    getRoles,
    getCompanyStepExceptions,
    getYardStepsPage,
    replaceCompanyStepExceptions,
    getCarrierGroups,
    getCarrierGroupRules,
    getFlows,
    replaceComposition,
    updateFlow,
    updateCarrierGroup,
    updateCarrierGroupRule,
  } = useVehicleJourneyConfiguration();
  const [flows, setFlows] = useState<YardFlow[]>([]);
  const [carrierGroups, setCarrierGroups] = useState<YardCatalogItem[]>([]);
  const [yardRoles, setYardRoles] = useState<YardRole[]>([]);
  const [catalogs, setCatalogs] =
    useState<VehicleJourneyCatalogs>(emptyCatalogs);
  const [selectedFlowId, setSelectedFlowId] = useState<number | null>(null);
  const [draft, setDraft] = useState<JourneyFlow>(emptyJourneyFlow);
  const [flowSearch, setFlowSearch] = useState('');
  const [stepSearch, setStepSearch] = useState('');
  const [isCreatingFlow, setIsCreatingFlow] = useState(false);
  const [flowErrors, setFlowErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedStepIds, setExpandedStepIds] = useState<string[]>([]);
  const [isCreatingStep, setIsCreatingStep] = useState(false);
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [newStepName, setNewStepName] = useState('');
  const [newStepCode, setNewStepCode] = useState('');
  const [newStepType, setNewStepType] = useState<YardStepType>('COMUM');
  const [newStepIndicatorRole, setNewStepIndicatorRole] =
    useState<YardDashboardIndicatorRole | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    let mounted = true;

    const safeLoad = <T,>(promise: Promise<T>): Promise<T | undefined> =>
      promise.catch((error) => {
        notify('error', apiErrorHandler(error).errorMessage);
        return undefined;
      });

    Promise.all([
      safeLoad(getFlows()),
      safeLoad(getCatalogs()),
      safeLoad(getCarrierGroups()),
      safeLoad(getRoles()),
    ])
      .then(
        ([loadedFlows, loadedCatalogs, loadedCarrierGroups, loadedRoles]) => {
          if (!mounted) return;
          if (loadedFlows) setFlows(loadedFlows);
          if (loadedCatalogs) setCatalogs(loadedCatalogs);
          if (loadedCarrierGroups) setCarrierGroups(loadedCarrierGroups);
          if (loadedRoles) setYardRoles(loadedRoles);
          if (loadedFlows && loadedFlows[0]) {
            setSelectedFlowId(loadedFlows[0].id);
            setDraft(toJourneyFlow(loadedFlows[0]));
          }
        }
      )
      .finally(() => mounted && setIsLoading(false));

    return () => {
      mounted = false;
    };
  }, [getCarrierGroups, getCatalogs, getFlows, getRoles]);

  const handleGroupSaved = (savedGroup: YardCatalogItem) => {
    setCarrierGroups((current) =>
      [
        ...current.filter((group) => group.id !== savedGroup.id),
        savedGroup,
      ].sort((a, b) => a.name.localeCompare(b.name))
    );
    setCatalogs((current) => ({
      ...current,
      carrierGroups: [
        ...current.carrierGroups.filter((group) => group.id !== savedGroup.id),
        ...(savedGroup.active ? [savedGroup] : []),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  };

  const clearFlowError = (field: string) => {
    setFlowErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const visibleFlows = useMemo(
    () =>
      flows.filter((flow) =>
        `${flow.code} ${flow.name}`
          .toLocaleLowerCase()
          .includes(flowSearch.toLocaleLowerCase())
      ),
    [flowSearch, flows]
  );

  const visibleCatalogSteps = useMemo(
    () =>
      catalogs.steps.filter(
        (step) =>
          !draft.steps.some((flowStep) => flowStep.yardStepId === step.id) &&
          `${step.code} ${step.name}`
            .toLocaleLowerCase()
            .includes(stepSearch.toLocaleLowerCase())
      ),
    [catalogs.steps, draft.steps, stepSearch]
  );

  const selectFlow = (flow: YardFlow) => {
    setSelectedFlowId(flow.id);
    setDraft(toJourneyFlow(flow));
    setIsCreatingFlow(false);
    setFlowErrors({});
    setStepSearch('');
    setExpandedStepIds([]);
  };

  const startNewFlow = () => {
    setDraft(emptyJourneyFlow());
    setIsCreatingFlow(true);
    setFlowErrors({});
    setExpandedStepIds([]);
  };

  const cancelChanges = () => {
    const selectedFlow = flows.find((flow) => flow.id === selectedFlowId);
    if (selectedFlow) {
      selectFlow(selectedFlow);
      return;
    }

    setDraft(emptyJourneyFlow());
    setIsCreatingFlow(false);
    setFlowErrors({});
    setExpandedStepIds([]);
  };

  const updateDraft = <K extends keyof JourneyFlow>(
    field: K,
    value: JourneyFlow[K]
  ) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const addStep = (yardStepId: number) => {
    const catalogStep = catalogs.steps.find((step) => step.id === yardStepId);
    if (
      !catalogStep ||
      draft.steps.some((step) => step.yardStepId === yardStepId)
    )
      return;

    const hasEntryAuthorizedOwner = draft.steps.some(
      (step) => step.completionTrigger === 'ENTRY_AUTHORIZED'
    );
    const completionTrigger =
      catalogStep.type === 'PATIO_EXTERNO' && !hasEntryAuthorizedOwner
        ? 'ENTRY_AUTHORIZED'
        : null;

    const step: JourneyStep = {
      id: temporaryId('step'),
      yardStepId: catalogStep.id,
      yardRoleId: null,
      responsibleRole: null,
      code: catalogStep.code,
      name: catalogStep.name,
      type: catalogStep.type,
      interactionMode: draft.steps.length ? 'CONFIRM' : 'START_FINISH',
      completionTrigger,
      hasBip: false,
      hasCoordinates: false,
      hasReject: false,
      required: true,
      active: true,
      fields: [],
    };
    updateDraft('steps', [...draft.steps, step]);
    setExpandedStepIds((current) => [...current, step.id]);
  };

  const cancelStepCreation = () => {
    if (isSavingStep) return;
    setIsCreatingStep(false);
    setNewStepName('');
    setNewStepCode('');
    setNewStepType('COMUM');
    setNewStepIndicatorRole(null);
  };

  const saveStep = async () => {
    const name = newStepName.trim();
    const code = newStepCode.trim();
    if (!name || !code || isSavingStep) return;

    setIsSavingStep(true);
    try {
      const step = await createStep({
        name,
        code,
        description: null,
        active: true,
        type: newStepType,
        indicatorRole: newStepIndicatorRole,
      });
      setCatalogs((current) => ({
        ...current,
        steps: [...current.steps, step].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      }));
      setIsCreatingStep(false);
      setNewStepName('');
      setNewStepCode('');
      setNewStepType('COMUM');
      setNewStepIndicatorRole(null);
      notify('success', 'Etapa criada com sucesso');
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSavingStep(false);
    }
  };

  const updateStep = (stepId: string, changes: Partial<JourneyStep>) => {
    updateDraft(
      'steps',
      draft.steps.map((step) =>
        step.id === stepId ? { ...step, ...changes } : step
      )
    );
  };

  const removeStep = (stepId: string) => {
    updateDraft(
      'steps',
      draft.steps.filter((step) => step.id !== stepId)
    );
    setExpandedStepIds((current) => current.filter((id) => id !== stepId));
  };

  const toggleStep = (stepId: string) => {
    setExpandedStepIds((current) =>
      current.includes(stepId)
        ? current.filter((id) => id !== stepId)
        : [...current, stepId]
    );
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    const destination = index + direction;
    if (destination < 0 || destination >= draft.steps.length) return;
    const steps = [...draft.steps];
    [steps[index], steps[destination]] = [steps[destination], steps[index]];
    updateDraft('steps', steps);
  };

  const addField = (stepId: string) => {
    const field: JourneyField = {
      id: temporaryId('field'),
      code: '',
      name: '',
      type: 'TEXT',
      required: false,
      active: true,
      placeholder: '',
      helpText: '',
      optionsText: '',
      options: [],
    };
    const step = draft.steps.find((item) => item.id === stepId);
    if (step) updateStep(stepId, { fields: [...step.fields, field] });
  };

  const updateField = (
    stepId: string,
    fieldId: string,
    changes: Partial<JourneyField>
  ) => {
    const step = draft.steps.find((item) => item.id === stepId);
    if (!step) return;
    updateStep(stepId, {
      fields: step.fields.map((field) =>
        field.id === fieldId ? { ...field, ...changes } : field
      ),
    });
  };

  const removeField = (stepId: string, fieldId: string) => {
    const step = draft.steps.find((item) => item.id === stepId);
    if (step) {
      updateStep(stepId, {
        fields: step.fields.filter((field) => field.id !== fieldId),
      });
    }
  };

  const updateOptions = (
    stepId: string,
    field: JourneyField,
    value: string
  ) => {
    const options = value
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean)
      .map((label, index) => ({
        id: field.options[index]?.id || temporaryId('option'),
        code: normalizeCode(label).slice(0, 50),
        label,
        active: true,
      }));
    updateField(stepId, field.id, { options, optionsText: value });
  };

  const saveFlow = async () => {
    const errors = await validateJourneyFlow({
      name: draft.name.trim(),
      code: draft.code.trim(),
      warehouse: draft.warehouseId,
      orderType: draft.orderTypeId,
      carrierType: draft.carrierGroupId,
    });
    setFlowErrors(errors);
    if (Object.keys(errors).length) return;

    const compositionError = validateComposition(draft);
    if (compositionError) {
      notify('error', compositionError);
      return;
    }

    setIsSaving(true);
    try {
      const header = {
        warehouseId:
          draft.warehouseId === '' ? null : Number(draft.warehouseId),
        orderTypeId: Number(draft.orderTypeId),
        carrierGroupId: Number(draft.carrierGroupId),
        code: draft.code.trim(),
        name: draft.name.trim(),
        description: null,
        active: draft.active,
      };
      const isNewFlow = !draft.id;
      const savedHeader = isNewFlow
        ? await createFlow(header)
        : await updateFlow(draft.id as number, header);
      try {
        const savedFlow = await replaceComposition(
          savedHeader.id,
          compositionPayload(draft)
        );

        setFlows((current) => {
          const withoutSaved = current.filter(
            (flow) => flow.id !== savedFlow.id
          );
          return [...withoutSaved, savedFlow].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
        });
        setSelectedFlowId(savedFlow.id);
        setDraft(toJourneyFlow(savedFlow));
        setIsCreatingFlow(false);
        setFlowErrors({});
        setExpandedStepIds([]);
        notify('success', 'Fluxo salvo com sucesso');
      } catch (compositionError) {
        if (isNewFlow) {
          try {
            await deleteFlow(savedHeader.id);
          } catch {
            // The header removal is best-effort; the original error is the relevant one.
          }
        }
        throw compositionError;
      }
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Scaffold>
      <S.MainPanel className="min-h-full">
        <ListingPageHeader
          icon={<S.SettingsIcon />}
          title="Jornada do veículo"
          isLoading={isLoading || isSaving}
        />
        <main className="flex w-full flex-1 flex-col overflow-hidden">
          <TabsController onTabChange={setActiveTab}>
            {[
              <ModalTab key="flow" title="Fluxo" />,
              <ModalTab
                key="carrier-groups"
                title="Agrupamento Transportadora"
              />,
              <ModalTab
                key="customer-exceptions"
                title="Exceções por cliente"
              />,
            ]}
          </TabsController>
          {activeTab === 0 ? (
            <div className="flex min-h-[calc(100vh-205px)] flex-1 overflow-hidden">
              <aside className="flex w-72 shrink-0 flex-col border-r border-slate-200 bg-white">
                <div className="border-b border-slate-200 p-3">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-base font-bold text-slate-900">
                      Fluxos
                    </h2>
                    <button
                      aria-label="Novo fluxo"
                      className="flex h-7 w-7 items-center justify-center rounded bg-[#EE7C2C] text-white"
                      onClick={startNewFlow}
                      type="button"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="relative">
                    <Search
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                      size={15}
                    />
                    <input
                      className="w-full rounded border border-slate-300 py-1.5 pl-8 pr-2 text-sm outline-none focus:border-[#EE7C2C]"
                      placeholder="Filtrar..."
                      value={flowSearch}
                      onChange={(event) => setFlowSearch(event.target.value)}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {isCreatingFlow && (
                    <form
                      aria-label="Novo fluxo"
                      className="relative border-b border-slate-200 bg-orange-50 p-3 pl-4"
                      onSubmit={(event) => {
                        event.preventDefault();
                        saveFlow();
                      }}
                    >
                      <span className="absolute bottom-0 left-0 top-0 w-1 bg-[#EE7C2C]" />
                      <label className="absolute right-3 top-3 flex cursor-pointer items-center gap-1.5 text-[9px] font-bold text-slate-500">
                        <span>{draft.active ? 'ATIVO' : 'INATIVO'}</span>
                        <span className="relative inline-flex">
                          <input
                            aria-label="Fluxo ativo"
                            checked={draft.active}
                            className="peer sr-only"
                            onChange={(event) =>
                              updateDraft('active', event.target.checked)
                            }
                            type="checkbox"
                          />
                          <span className="h-4 w-7 rounded-full bg-slate-300 transition-colors peer-checked:bg-[#EE7C2C]" />
                          <span className="absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform peer-checked:translate-x-3" />
                        </span>
                      </label>
                      <label className="block">
                        <span className="sr-only">Nome do fluxo</span>
                        <input
                          className="w-full border-0 border-b border-orange-200 bg-transparent py-1 pl-0 pr-24 text-sm font-bold text-slate-800 outline-none placeholder:text-slate-400 focus:border-[#EE7C2C]"
                          placeholder="Informe o nome do fluxo"
                          value={draft.name}
                          onChange={(event) => {
                            const name = sanitizeFlowName(event.target.value);
                            updateDraft('name', name);
                            updateDraft('code', normalizeCode(name));
                            clearFlowError('name');
                            clearFlowError('code');
                          }}
                        />
                      </label>
                      {flowErrors.name && (
                        <p className="mt-1 text-xs font-bold italic text-red-600">
                          {flowErrors.name}
                        </p>
                      )}
                      <label className="mt-1 block">
                        <span className="sr-only">Código do fluxo</span>
                        <input
                          className="w-full border-0 border-b border-orange-200 bg-transparent px-0 py-1 font-mono text-[11px] font-semibold text-slate-500 outline-none placeholder:text-slate-400 focus:border-[#EE7C2C]"
                          placeholder="Informe o código do fluxo"
                          value={draft.code}
                          onChange={(event) => {
                            updateDraft(
                              'code',
                              normalizeCode(event.target.value)
                            );
                            clearFlowError('code');
                          }}
                        />
                      </label>
                      {flowErrors.code && (
                        <p className="mt-1 text-xs font-bold italic text-red-600">
                          {flowErrors.code}
                        </p>
                      )}
                    </form>
                  )}
                  {visibleFlows.map((flow) => (
                    <button
                      aria-pressed={
                        !isCreatingFlow && flow.id === selectedFlowId
                      }
                      key={flow.id}
                      onClick={() => selectFlow(flow)}
                      className={`relative block w-full border-b border-slate-200 p-3 text-left transition-colors ${
                        !isCreatingFlow && flow.id === selectedFlowId
                          ? 'bg-orange-50'
                          : 'hover:bg-slate-50'
                      }`}
                      type="button"
                    >
                      {!isCreatingFlow && flow.id === selectedFlowId && (
                        <span className="absolute bottom-0 left-0 top-0 w-1 bg-[#EE7C2C]" />
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <strong className="truncate text-sm text-slate-800">
                          {flow.name}
                        </strong>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${flow.active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}
                        >
                          {flow.active ? 'ATIVO' : 'INATIVO'}
                        </span>
                      </div>
                      <span className="mt-0.5 block truncate font-mono text-[11px] font-semibold text-slate-500">
                        {flow.code}
                      </span>
                    </button>
                  ))}
                  {!isLoading && !visibleFlows.length && !isCreatingFlow && (
                    <p className="p-4 text-center text-xs text-slate-500">
                      Nenhum fluxo encontrado.
                    </p>
                  )}
                </div>
              </aside>

              <section className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4">
                  <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-slate-800">
                    <Settings size={18} className="text-[#EE7C2C]" /> Seletores
                    do fluxo
                  </h3>
                  <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Armazém
                      </span>
                      <select
                        aria-label="Armazém"
                        className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={draft.warehouseId}
                        onChange={(event) => {
                          updateDraft('warehouseId', event.target.value);
                          clearFlowError('warehouse');
                        }}
                      >
                        <option value="">Selecione</option>
                        {catalogs.warehouses.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      {flowErrors.warehouse && (
                        <span className="text-xs font-bold italic text-red-600">
                          {flowErrors.warehouse}
                        </span>
                      )}
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Tipo de Ordem
                      </span>
                      <select
                        aria-label="Tipo de Ordem"
                        className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={draft.orderTypeId}
                        onChange={(event) => {
                          updateDraft('orderTypeId', event.target.value);
                          clearFlowError('orderType');
                        }}
                      >
                        <option value="">Selecione</option>
                        {catalogs.orderTypes.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      {flowErrors.orderType && (
                        <span className="text-xs font-bold italic text-red-600">
                          {flowErrors.orderType}
                        </span>
                      )}
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Grupo de Transportadora
                      </span>
                      <select
                        aria-label="Grupo de Transportadora"
                        className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={draft.carrierGroupId}
                        onChange={(event) => {
                          updateDraft('carrierGroupId', event.target.value);
                          clearFlowError('carrierType');
                        }}
                      >
                        <option value="">Selecione</option>
                        {catalogs.carrierGroups.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      {flowErrors.carrierType && (
                        <span className="text-xs font-bold italic text-red-600">
                          {flowErrors.carrierType}
                        </span>
                      )}
                    </label>
                  </div>

                  <div className="overflow-hidden rounded border border-slate-200 bg-white">
                    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">
                          {draft.code || 'Novo fluxo'}
                        </h2>
                        <p className="mt-1 text-sm font-semibold text-slate-500">
                          {draft.name}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          mood="light"
                          size="small"
                          type="button"
                          onClick={cancelChanges}
                        >
                          Cancelar
                        </Button>
                        <Button
                          mood="primary"
                          onClick={saveFlow}
                          size="small"
                          type="button"
                          disabled={isSaving || isLoading}
                        >
                          <Check size={16} />{' '}
                          {isSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </div>
                    </header>
                    <div className="grid min-h-[430px] grid-cols-1 gap-4 bg-slate-50 p-4 lg:grid-cols-2">
                      <div className="flex flex-col rounded border border-slate-200">
                        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 p-3">
                          <h3 className="text-sm font-bold text-slate-800">
                            Catálogo de Etapas
                          </h3>
                          <button
                            aria-label="Nova etapa"
                            className="flex h-7 w-7 items-center justify-center rounded bg-[#EE7C2C] text-white"
                            onClick={() => setIsCreatingStep(true)}
                            type="button"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        {isCreatingStep && (
                          <form
                            aria-label="Nova etapa"
                            className="border-b border-slate-200 bg-orange-50 p-2"
                            onBlur={(event) => {
                              if (
                                !event.currentTarget.contains(
                                  event.relatedTarget
                                )
                              ) {
                                cancelStepCreation();
                              }
                            }}
                            onSubmit={(event) => {
                              event.preventDefault();
                              saveStep();
                            }}
                          >
                            <input
                              autoFocus
                              aria-label="Nome da nova etapa"
                              className="w-full rounded border border-orange-200 bg-white px-2 py-1.5 text-xs font-semibold outline-none focus:border-[#EE7C2C]"
                              placeholder="Nome da etapa"
                              value={newStepName}
                              onChange={(event) => {
                                const name = sanitizeFlowName(
                                  event.target.value
                                );
                                setNewStepName(name);
                                setNewStepCode(normalizeCode(name));
                              }}
                            />
                            <input
                              aria-label="Código da nova etapa"
                              className="mt-2 w-full rounded border border-orange-200 bg-white px-2 py-1.5 font-mono text-[11px] outline-none focus:border-[#EE7C2C]"
                              placeholder="CODIGO_DA_ETAPA"
                              value={newStepCode}
                              onChange={(event) =>
                                setNewStepCode(
                                  normalizeCode(event.target.value)
                                )
                              }
                            />
                            <label className="mt-2 block text-[11px] font-semibold text-slate-700">
                              Tipo da etapa
                              <select
                                aria-label="Tipo da etapa"
                                className="mt-1 w-full rounded border border-orange-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-[#EE7C2C]"
                                onChange={(event) =>
                                  setNewStepType(
                                    event.target.value as YardStepType
                                  )
                                }
                                value={newStepType}
                              >
                                {STEP_TYPES.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="mt-2 block text-[11px] font-semibold text-slate-700">
                              Papel no indicador
                              <select
                                aria-label="Papel no indicador"
                                className="mt-1 w-full rounded border border-orange-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-[#EE7C2C]"
                                onChange={(event) =>
                                  setNewStepIndicatorRole(
                                    (event.target.value ||
                                      null) as YardDashboardIndicatorRole | null
                                  )
                                }
                                value={newStepIndicatorRole ?? ''}
                              >
                                <option value="">Nenhum</option>
                                {INDICATOR_ROLES.map((role) => (
                                  <option key={role.value} value={role.value}>
                                    {role.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <div className="mt-2 flex justify-end gap-2">
                              <button
                                className="rounded px-2 py-1 text-[10px] font-bold text-slate-500"
                                onClick={cancelStepCreation}
                                type="button"
                              >
                                Cancelar
                              </button>
                              <button
                                className="rounded bg-[#EE7C2C] px-2 py-1 text-[10px] font-bold text-white disabled:opacity-50"
                                disabled={
                                  !newStepName.trim() ||
                                  !newStepCode.trim() ||
                                  isSavingStep
                                }
                                type="submit"
                              >
                                {isSavingStep ? 'Criando...' : 'Criar'}
                              </button>
                            </div>
                          </form>
                        )}
                        <div className="border-b border-slate-200 p-2">
                          <div className="relative">
                            <Search
                              className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                              size={14}
                            />
                            <input
                              className="w-full rounded border border-slate-300 py-1.5 pl-7 pr-2 text-sm"
                              placeholder="Buscar etapa..."
                              value={stepSearch}
                              onChange={(event) =>
                                setStepSearch(event.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2">
                          {visibleCatalogSteps.map((step) => (
                            <button
                              className="mb-2 flex w-full items-center justify-between rounded border border-slate-200 bg-white p-2 text-left shadow-sm"
                              key={step.id}
                              onClick={() => addStep(step.id)}
                              type="button"
                            >
                              <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                                <strong className="block text-xs text-slate-800">
                                  {step.name}
                                </strong>
                                {step.type !== 'COMUM' && (
                                  <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-blue-700">
                                    {STEP_TYPE_LABELS[step.type]}
                                  </span>
                                )}
                              </span>
                              <Plus
                                className="shrink-0 text-[#EE7C2C]"
                                size={17}
                              />
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-col rounded border border-slate-200">
                        <div className="border-b border-slate-200 bg-slate-50 p-3">
                          <h3 className="text-sm font-bold text-slate-800">
                            Sequência
                          </h3>
                        </div>
                        <div className="flex-1 overflow-y-auto bg-slate-50 p-3">
                          {draft.steps.map((step, index) => {
                            const expanded = expandedStepIds.includes(step.id);

                            return (
                              <div
                                className="relative mb-3 flex gap-2"
                                key={step.id}
                              >
                                <div
                                  className={`z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${step.active ? 'bg-[#EE7C2C] text-white' : 'bg-slate-200 text-slate-600'}`}
                                >
                                  {index + 1}
                                </div>
                                <div className="min-w-0 flex-1 rounded border border-slate-200 bg-white p-2 shadow-sm">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <GripVertical
                                      className="text-slate-400"
                                      size={16}
                                    />
                                    <button
                                      aria-expanded={expanded}
                                      className="flex min-w-[100px] flex-1 items-center gap-1 overflow-hidden text-left"
                                      onClick={() => toggleStep(step.id)}
                                      type="button"
                                    >
                                      <ChevronRight
                                        className={`shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
                                        size={15}
                                      />
                                      <span className="min-w-0 flex-1 truncate">
                                        <strong className="block truncate text-xs text-slate-800">
                                          {step.name}
                                        </strong>
                                      </span>
                                    </button>
                                    {step.type !== 'COMUM' && (
                                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-blue-700">
                                        {STEP_TYPE_LABELS[step.type]}
                                      </span>
                                    )}
                                    <button
                                      aria-label={`Subir ${step.name}`}
                                      disabled={index === 0}
                                      onClick={() => moveStep(index, -1)}
                                      type="button"
                                    >
                                      <ChevronUp size={15} />
                                    </button>
                                    <button
                                      aria-label={`Descer ${step.name}`}
                                      disabled={
                                        index === draft.steps.length - 1
                                      }
                                      onClick={() => moveStep(index, 1)}
                                      type="button"
                                    >
                                      <ChevronDown size={15} />
                                    </button>
                                    <button
                                      aria-label={`Remover ${step.name}`}
                                      onClick={() => removeStep(step.id)}
                                      type="button"
                                    >
                                      <Trash2
                                        className="text-red-500"
                                        size={14}
                                      />
                                    </button>
                                  </div>
                                  {expanded && (
                                    <div className="mt-3 border-t border-slate-100 pt-3">
                                      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                        Configurações
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 rounded border border-slate-100 bg-slate-50 p-2 text-[10px] text-slate-600">
                                        <label>
                                          Interação{' '}
                                          <select
                                            className="ml-1 rounded border border-slate-300"
                                            value={step.interactionMode}
                                            onChange={(event) =>
                                              updateStep(step.id, {
                                                interactionMode: event.target
                                                  .value as JourneyStep['interactionMode'],
                                              })
                                            }
                                          >
                                            <option value="START_FINISH">
                                              Iniciar/finalizar
                                            </option>
                                            <option value="CONFIRM">
                                              Confirmar
                                            </option>
                                          </select>
                                        </label>
                                        {step.type === 'PATIO_EXTERNO' && (
                                          <>
                                            <label>
                                              Conclusão da etapa{' '}
                                              <select
                                                aria-label={`Conclusão da etapa ${step.name}`}
                                                className="ml-1 rounded border border-slate-300"
                                                value={
                                                  step.completionTrigger ?? ''
                                                }
                                                onChange={(event) => {
                                                  const hasOtherOwner =
                                                    draft.steps.some(
                                                      (draftStep) =>
                                                        draftStep.id !==
                                                          step.id &&
                                                        draftStep.completionTrigger ===
                                                          'ENTRY_AUTHORIZED'
                                                    );
                                                  const completionTrigger =
                                                    event.target.value ===
                                                      'ENTRY_AUTHORIZED' &&
                                                    !hasOtherOwner
                                                      ? 'ENTRY_AUTHORIZED'
                                                      : null;
                                                  updateStep(step.id, {
                                                    completionTrigger,
                                                    active:
                                                      completionTrigger ===
                                                      'ENTRY_AUTHORIZED'
                                                        ? true
                                                        : step.active,
                                                    hasCoordinates:
                                                      completionTrigger ===
                                                      'ENTRY_AUTHORIZED'
                                                        ? false
                                                        : step.hasCoordinates,
                                                    fields:
                                                      completionTrigger ===
                                                      'ENTRY_AUTHORIZED'
                                                        ? step.fields.map(
                                                            (field) => ({
                                                              ...field,
                                                              required: false,
                                                            })
                                                          )
                                                        : step.fields,
                                                  });
                                                }}
                                              >
                                                <option value="">
                                                  Pela Jornada
                                                </option>
                                                <option
                                                  disabled={draft.steps.some(
                                                    (draftStep) =>
                                                      draftStep.id !==
                                                        step.id &&
                                                      draftStep.completionTrigger ===
                                                        'ENTRY_AUTHORIZED'
                                                  )}
                                                  value="ENTRY_AUTHORIZED"
                                                >
                                                  Ao liberar entrada na Portaria
                                                </option>
                                              </select>
                                            </label>
                                            {step.completionTrigger ===
                                              'ENTRY_AUTHORIZED' && (
                                              <>
                                                <p className="col-span-2 text-[10px] text-slate-500">
                                                  Quando a Portaria liberar a
                                                  entrada do veículo, esta etapa
                                                  será concluída
                                                  automaticamente.
                                                </p>
                                                <p className="col-span-2 text-[10px] text-slate-500">
                                                  A conclusão automática exige
                                                  que a etapa esteja ativa e não
                                                  permite coordenadas nem campos
                                                  obrigatórios.
                                                </p>
                                              </>
                                            )}
                                          </>
                                        )}
                                        <label>
                                          <input
                                            checked={step.required}
                                            onChange={(event) =>
                                              updateStep(step.id, {
                                                required: event.target.checked,
                                              })
                                            }
                                            type="checkbox"
                                          />{' '}
                                          Obrigatória
                                        </label>
                                        <label>
                                          <input
                                            checked={
                                              step.completionTrigger ===
                                              'ENTRY_AUTHORIZED'
                                                ? true
                                                : step.active
                                            }
                                            disabled={
                                              step.completionTrigger ===
                                              'ENTRY_AUTHORIZED'
                                            }
                                            onChange={(event) =>
                                              updateStep(step.id, {
                                                active:
                                                  step.completionTrigger ===
                                                  'ENTRY_AUTHORIZED'
                                                    ? true
                                                    : event.target.checked,
                                              })
                                            }
                                            type="checkbox"
                                          />{' '}
                                          Ativa
                                        </label>
                                        <label>
                                          <input
                                            checked={step.hasBip}
                                            onChange={(event) =>
                                              updateStep(step.id, {
                                                hasBip: event.target.checked,
                                              })
                                            }
                                            type="checkbox"
                                          />{' '}
                                          Permite bip
                                        </label>
                                        <label>
                                          <input
                                            checked={step.hasCoordinates}
                                            disabled={
                                              step.completionTrigger ===
                                              'ENTRY_AUTHORIZED'
                                            }
                                            onChange={(event) =>
                                              updateStep(step.id, {
                                                hasCoordinates:
                                                  step.completionTrigger ===
                                                  'ENTRY_AUTHORIZED'
                                                    ? false
                                                    : event.target.checked,
                                              })
                                            }
                                            type="checkbox"
                                          />{' '}
                                          Exige coordenadas
                                        </label>
                                        <label>
                                          <input
                                            checked={step.hasReject}
                                            onChange={(event) =>
                                              updateStep(step.id, {
                                                hasReject: event.target.checked,
                                              })
                                            }
                                            type="checkbox"
                                          />{' '}
                                          Permite rejeição
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                  {expanded && (
                                    <div className="mt-3 border-t border-slate-100 pt-3">
                                      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                        Responsável
                                      </div>
                                      <label className="block text-xs text-slate-600">
                                        Grupo responsável pela etapa
                                        <select
                                          aria-label={`Grupo responsável pela etapa ${step.name}`}
                                          className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700"
                                          value={
                                            step.yardRoleId === null
                                              ? ''
                                              : String(step.yardRoleId)
                                          }
                                          onChange={(event) => {
                                            const yardRoleId = event.target
                                              .value
                                              ? Number(event.target.value)
                                              : null;
                                            updateStep(step.id, {
                                              yardRoleId,
                                              responsibleRole:
                                                yardRoles.find(
                                                  (role) =>
                                                    role.id === yardRoleId
                                                ) ?? null,
                                            });
                                          }}
                                        >
                                          <option value="">
                                            Sem grupo — somente administradores
                                          </option>
                                          {step.responsibleRole &&
                                            !yardRoles.some(
                                              (role) =>
                                                role.id ===
                                                step.responsibleRole?.id
                                            ) && (
                                              <option
                                                value={step.responsibleRole.id}
                                              >
                                                {step.responsibleRole.name}{' '}
                                                (inativo)
                                              </option>
                                            )}
                                          {yardRoles.map((role) => (
                                            <option
                                              key={role.id}
                                              value={role.id}
                                            >
                                              {role.name}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                      <p className="mt-1 text-[10px] text-slate-500">
                                        Usuários da Portaria só poderão executar
                                        as ações da etapa quando pertencerem a
                                        este grupo.
                                      </p>
                                    </div>
                                  )}
                                  {expanded && (
                                    <div className="mt-3 border-t border-slate-100 pt-3">
                                      <div className="mb-2 flex items-center justify-between">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                          Campos da Etapa
                                        </span>
                                        <button
                                          className="flex items-center gap-1 text-[10px] font-bold text-[#EE7C2C]"
                                          onClick={() => addField(step.id)}
                                          type="button"
                                        >
                                          <Plus size={13} /> Adicionar campo
                                        </button>
                                      </div>
                                      {step.fields.map((field) => (
                                        <div
                                          className="mb-3 rounded border border-slate-200 p-2"
                                          key={field.id}
                                        >
                                          <div className="grid grid-cols-2 gap-2">
                                            <input
                                              aria-label="Nome do campo"
                                              className="rounded border border-slate-300 px-2 py-1 text-xs"
                                              placeholder="Nome do campo"
                                              value={field.name}
                                              onChange={(event) => {
                                                const name = sanitizeFlowName(
                                                  event.target.value
                                                );
                                                updateField(step.id, field.id, {
                                                  name,
                                                  code: normalizeCode(name),
                                                });
                                              }}
                                            />
                                            <input
                                              aria-label="Código do campo"
                                              className="rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                                              placeholder="CODIGO_CAMPO"
                                              value={field.code}
                                              onChange={(event) =>
                                                updateField(step.id, field.id, {
                                                  code: normalizeCode(
                                                    event.target.value
                                                  ),
                                                })
                                              }
                                            />
                                            <select
                                              aria-label="Tipo do campo"
                                              className="rounded border border-slate-300 px-2 py-1 text-xs"
                                              value={field.type}
                                              onChange={(event) =>
                                                updateField(step.id, field.id, {
                                                  type: event.target
                                                    .value as JourneyField['type'],
                                                  optionsText: '',
                                                  options: [],
                                                })
                                              }
                                            >
                                              {FIELD_TYPES.map((type) => (
                                                <option
                                                  key={type.value}
                                                  value={type.value}
                                                >
                                                  {type.label}
                                                </option>
                                              ))}
                                            </select>
                                            <div className="flex items-center justify-between text-[10px] text-slate-600">
                                              <label>
                                                <input
                                                  checked={field.required}
                                                  disabled={
                                                    step.completionTrigger ===
                                                    'ENTRY_AUTHORIZED'
                                                  }
                                                  onChange={(event) =>
                                                    updateField(
                                                      step.id,
                                                      field.id,
                                                      {
                                                        required:
                                                          step.completionTrigger ===
                                                          'ENTRY_AUTHORIZED'
                                                            ? false
                                                            : event.target
                                                                .checked,
                                                      }
                                                    )
                                                  }
                                                  type="checkbox"
                                                />{' '}
                                                Obrigatório
                                              </label>
                                              <label>
                                                <input
                                                  checked={field.active}
                                                  onChange={(event) =>
                                                    updateField(
                                                      step.id,
                                                      field.id,
                                                      {
                                                        active:
                                                          event.target.checked,
                                                      }
                                                    )
                                                  }
                                                  type="checkbox"
                                                />{' '}
                                                Ativo
                                              </label>
                                              <button
                                                aria-label={`Remover campo ${field.name}`}
                                                onClick={() =>
                                                  removeField(step.id, field.id)
                                                }
                                                type="button"
                                              >
                                                <Trash2
                                                  className="text-red-500"
                                                  size={14}
                                                />
                                              </button>
                                            </div>
                                          </div>
                                          {fieldHasOptions(field) && (
                                            <input
                                              aria-label="Opções do campo"
                                              className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                                              placeholder="Opções separadas por vírgula"
                                              value={field.optionsText}
                                              onChange={(event) =>
                                                updateOptions(
                                                  step.id,
                                                  field,
                                                  event.target.value
                                                )
                                              }
                                            />
                                          )}
                                          <div className="mt-2">
                                            {fieldPreview(field)}
                                          </div>
                                        </div>
                                      ))}
                                      {!step.fields.length && (
                                        <p className="rounded border border-dashed border-slate-200 p-3 text-center text-[10px] text-slate-500">
                                          Nenhum campo configurado nesta etapa.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {!draft.steps.length && (
                            <p className="py-10 text-center text-xs text-slate-500">
                              Adicione etapas do catálogo para montar o fluxo.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          ) : activeTab === 1 ? (
            <CarrierGroupsTab
              groups={carrierGroups}
              carriers={catalogs.carriers}
              createGroup={createCarrierGroup}
              updateGroup={updateCarrierGroup}
              getRules={getCarrierGroupRules}
              createRule={createCarrierGroupRule}
              updateRule={updateCarrierGroupRule}
              onGroupSaved={handleGroupSaved}
            />
          ) : (
            <CustomerExceptionsTab
              getCompanies={getCompanyStepExceptions}
              getSteps={getYardStepsPage}
              replaceExceptions={replaceCompanyStepExceptions}
            />
          )}
        </main>
      </S.MainPanel>
    </Scaffold>
  );
};
